// agent-decision-block — renders an agent_decision step's live choosing state
// and final decision. Uses spinner→swap pattern: "Đang chọn..." with 12px
// purple spinner while the step is thinking; swaps to "Đã chọn: {label}" pill
// + collapsible reasoning once decision_made arrives.
// kbRefs / kgRefs are optional — render ref strip only when BE ships them (A3).

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "use-intl";
import { cn } from "@/utils";
import type { StepStreamState } from "@/hooks/useWorkflowRunStream";
import type { KbRef, KgRef } from "@/types/workflow.types";
import { RefLinkStrip } from "../run-detail/ref-link-strip";

interface AgentDecisionBlockProps {
  streamState?: StepStreamState;
  /** KB source refs from agent.decide.complete payload — absent until BE A3 ships. */
  kbRefs?: KbRef[];
  /** KG entity refs from agent.decide.complete payload — absent until BE A3 ships. */
  kgRefs?: KgRef[];
  className?: string;
}

export function AgentDecisionBlock({ streamState, kbRefs, kgRefs, className }: AgentDecisionBlockProps) {
  const t = useTranslations("workflows");
  const [reasoningOpen, setReasoningOpen] = useState(false);

  const isThinking = !streamState?.decisionLabel && (
    streamState?.status === "thinking" ||
    streamState?.status === "running"
  );

  // Show placeholder if no stream data at all
  if (!streamState) {
    return (
      <span className={cn("text-[12px] text-[var(--muted-foreground)]", className)}>
        {t("run.decision.pending")}
      </span>
    );
  }

  // Spinner state — decision not yet made
  if (isThinking) {
    return (
      <span className={cn("flex items-center gap-1.5 text-[12px]", className)}>
        <span
          className="inline-block w-[12px] h-[12px] rounded-full border-2 border-[var(--purple)] border-t-transparent animate-[spin_0.9s_linear_infinite]"
          aria-hidden
        />
        <span className="text-[var(--purple)] font-medium">
          {t("run.decision.choosing")}
        </span>
      </span>
    );
  }

  // Decision timeout stub — FE-only, fires after 60s without decision_made
  if (streamState.status === "thinking" && !streamState.decisionLabel) {
    return (
      <span className={cn("text-[12px] text-[var(--warning)]", className)}>
        {t("run.decision.timeout_hint")}
      </span>
    );
  }

  // Decision made
  const label = streamState.decisionLabel;
  const reasoning = streamState.decisionReasoning;

  if (!label) {
    return (
      <span className={cn("text-[12px] text-[var(--muted-foreground)]", className)}>
        {t("run.decision.pending")}
      </span>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {/* Chosen pill */}
      <span className="inline-flex items-center gap-1.5 self-start">
        <span className="text-[12px] text-[var(--muted-foreground)]">
          {t("run.decision.chosen")}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-[var(--radius-full)] bg-[var(--purple-bg)] text-[var(--purple)] text-[11px] font-semibold leading-none">
          {label}
        </span>
      </span>

      {/* Reasoning collapse */}
      {reasoning && (
        <div>
          <button
            type="button"
            onClick={() => setReasoningOpen((o) => !o)}
            className={cn(
              "flex items-center gap-1 text-[11px] text-[var(--muted-foreground)]",
              "hover:text-[var(--foreground)] transition-colors duration-[120ms]"
            )}
          >
            <ChevronDown
              size={12}
              strokeWidth={1.6}
              aria-hidden
              className={cn(
                "transition-transform duration-[180ms]",
                reasoningOpen ? "-rotate-180" : "rotate-0"
              )}
            />
            {t("run.decision.reasoning_toggle")}
          </button>
          {reasoningOpen && (
            <p className="mt-1 text-[11px] text-[var(--muted-foreground)] leading-relaxed pl-4 border-l-2 border-[var(--border)] animate-[expand_180ms_cubic-bezier(0.2,0,0,1)_both]">
              {reasoning}
            </p>
          )}
        </div>
      )}

      {/* KB / KG ref strip — only when BE ships refs (A3). Skip if empty. */}
      <RefLinkStrip kbRefs={kbRefs} kgRefs={kgRefs} />
    </div>
  );
}
